<template>
  <ValidationObserver ref="observer" tag="div">
    <base-overlay-wrapper>
      <template v-for="(field, index) in fields">
        <base-field
          v-model="form[field.name]"
          :prev-value="prevValues[field.name]"
          :key="index"
          :field="field"
          :meta="meta"
          :ref="`field_${field.name}`"
        />
      </template>

      <base-overlay v-if="loading">
        <base-loading />
      </base-overlay>
    </base-overlay-wrapper>

    <v-alert v-if="error" type="error">
      {{ $t('common.savingError', { error }) }}
    </v-alert>

    <div v-if="showButtons" class="text-right">
      <v-btn color="primary" :loading="loading" :disabled="notChanged" @click="handleSave">
        {{ $t('common.save') }}
      </v-btn>

      <v-btn text class="ml-2" @click="handleReset">
        {{ $t('common.cancel') }}
      </v-btn>
    </div>
  </ValidationObserver>
</template>

<script>
import { ValidationObserver } from 'vee-validate';

import Candidate from '@/api/http/candidate';
import fieldsParams from '@/components/form-generator/constants/fields';

import BaseField from '@/components/form-generator/BaseField.vue';
import BaseOverlay from '@/components/base/BaseOverlay.vue';
import BaseLoading from '@/components/base/BaseLoading.vue';
import BaseOverlayWrapper from '@/components/base/BaseOverlayWrapper.vue';

export default {
  props: {
    meta: {
      type: Object,
    },
    fields: {
      type: Array,
      default: () => [],
    },
    initialValues: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      form: {},
      prevValues: {},
      error: false,
      loading: false,
    };
  },
  watch: {
    form: {
      deep: true,
      handler(value) {
        this.updateMeta(value);
      },
    },
  },
  beforeMount() {
    this.initFields();
    this.prepareForm();
  },
  computed: {
    showButtons() {
      return this.fields.map(({ disabled }) => !disabled).filter(Boolean).length > 0;
    },
    notChanged() {
      const changes = Object.entries(this.form).filter(([key, value]) => {
        return this.prevValues[key] !== value;
      });

      return changes.length === 0;
    },
  },
  methods: {
    parseValue({ value, name }) {
      const field = this.fieldsParams[name];

      if (!field) return;

      const { type = 'String' } = field;

      if (type === 'Boolean') {
        return value;
      }

      if (!value) {
        return this.initialValues[name] || null;
      }

      if (type === 'Number') {
        return Number(value);
      }

      return value;
    },
    initFields() {
      this.fields.forEach(field => {
        const value = this.parseValue(field);

        this.$set(this.prevValues, field.name, value);
      });

      this.$nextTick(() => {
        this.updateMeta(this.prevValues);
      });
    },
    prepareForm() {
      this.form = {};

      this.fields.forEach(field => {
        if (!field.value) {
          const value = this.initialValues[field.name] || null;

          if (value) this.$set(this.form, field.name, value);
        }
      });

      this.$nextTick(() => {
        this.updateMeta(this.prevValues);
      });
    },
    updateMeta(form) {
      this.$emit('updateMeta', form);
    },
    handleSave() {
      this.$refs.observer.validate().then(valid => {
        if (!valid) return;

        this.saveForm(valid);
      });
    },
    saveForm() {
      this.loading = true;

      const saveData = Object.entries(this.form).map(([name, value]) => {
        return {
          name,
          value,
        };
      });

      Candidate.saveCandidateForm(saveData)
        .then(({ data }) => {
          this.error = false;
          const { sections: schema } = data.result;

          this.$emit('updateSchema', schema);

          this.$nextTick(() => this.reRenderComponents());

          this.$toast.success(this.$t('common.savingSuccess'));
        })
        .catch(error => {
          this.error = error;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async handleReset() {
      this.error = false;
      this.prepareForm();

      this.fields.forEach(({ name }) => {
        const [field] = this.$refs[`field_${name}`];
        const isDocument = this.isDocument(name);

        if (field && isDocument) field.reRender();
        else if (field) field.returnOldValue();
      });

      this.updateMeta(this.prevValues);

      this.$nextTick(() => {
        this.$refs.observer.reset();
      });
    },
    reRenderComponents() {
      this.initFields();
      this.prepareForm();
      this.$refs.observer.reset();

      this.fields.forEach(({ name }) => {
        const [field] = this.$refs[`field_${name}`];
        const isDocument = this.isDocument(name);

        if (field && isDocument) field.reRender();
      });
    },
    isDocument(name) {
      const fieldProps = this.fieldsParams[name];
      return !!fieldProps && fieldProps.type === 'Array';
    },
  },
  mixins: [fieldsParams],
  components: {
    BaseOverlayWrapper,
    BaseLoading,
    BaseOverlay,
    BaseField,
    ValidationObserver,
  },
};
</script>

<style lang="scss" scoped></style>
